import { BottomBar, Box, CopyToClipboard, Gap, P, SaveBar, Tooltip, SavePanel } from '@salecycle/ui-component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router';
import { useHistory, useParams } from 'react-router-dom';
import { TemplateManagementRoute } from 'sections/design/template-management/routes';
import useUnloadTab from 'sections/hooks/useUnloadTab';
import { isPreviewMode, useTemplateEditorState } from '../context/UseTemplateEditorContext';
import { TooltipButton } from './common/TooltipButton';
import { TooltipHtml } from './common/TooltipHtml';
import { TemplateEditorSaveCommentModal } from './modals/TemplateEditorSaveCommentModal';
import { TemplateEditorSettingsModal } from './modals/TemplateEditorSettingsModal';
import { TemplateEditorUnsavedChangesModal } from './modals/TemplateEditorUnsavedChangesModal';
import { Circle } from './style';
import { useInitialPreviewTemplate } from '../hooks/useInitialPreviewTemplate';
import { useListenAndPreviewTemplate } from '../hooks/useListenAndMarkTemplateForPreview';
import { useRestoreTemplateVersion } from '../hooks/useRestoreTemplateVersion';
import { useDiscardChanges } from '../hooks/useDiscardChanges';
import { useSaveChanges } from '../hooks/useSaveChanges';

export const TemplateEditorBottomToolbar = () => {
  const { t } = useTranslation('designs');

  const {
    template,
    compilationContext,
    randomCompilationContextIndex,
    hasUnsavedChanges,
    editingInTab,
    previewingVersion: versionId,
  } = useTemplateEditorState();

  const { siteId, channel = template.channel } = useParams<{
    siteId: string;
    id: string;
    channel: string;
    language: string;
  }>();

  const { push } = useHistory();

  const [settingsModalOpen, setSettingsModelOpen] = useState(false);
  const [commentsModalOpen, setCommentsModelOpen] = useState(false);
  const [unsavedChangesModalOpen, setUnsavedChangesModalOpen] = useState(false);

  const restoreTemplateVersion = useRestoreTemplateVersion();
  const discardChanges = useDiscardChanges();
  const saveChanges = useSaveChanges();

  useInitialPreviewTemplate();
  useListenAndPreviewTemplate();
  useUnloadTab(hasUnsavedChanges);

  const handleSettingsButtonClicked = () => {
    setSettingsModelOpen(true);
  };

  const handleSaveButtonClicked = () => {
    setCommentsModelOpen(true);
  };

  const handleExitButtonClicked = () => {
    if (hasUnsavedChanges) {
      setUnsavedChangesModalOpen(true);
    } else {
      if (editingInTab) {
        saveChanges();
      } else {
        push(generatePath(TemplateManagementRoute.HOME, { siteId, channel }));
      }
    }
  };

  const options = () => {
    return (
      <Box verticalPositioning="center" horizontalPositioning="center">
        <TooltipButton
          icon="cog"
          tooltipHeading={t('designs:designGallery.templateEditor.bottomToolbar.settings')}
          tooltipDescription={t('designs:designGallery.templateEditor.bottomToolbar.settingsDescription')}
          tooltipPosition="top"
          onClick={() => handleSettingsButtonClicked()}
        />
        <Gap right={50} />
        {randomCompilationContextIndex > -1 ? (
          <Box verticalPositioning="center" horizontalPositioning="center">
            <Circle data-testid="SnapshotIndex">
              <P htmlTag="span">{randomCompilationContextIndex + 1}</P>
            </Circle>
            <Gap right={10} />
          </Box>
        ) : null}
        {compilationContext?.snapshotId ? (
          <Tooltip
            title={t('designs:designGallery.templateEditor.bottomToolbar.selectedCompilationContext.tooltipHeading')}
            html={
              <TooltipHtml
                heading={t(
                  'designs:designGallery.templateEditor.bottomToolbar.selectedCompilationContext.tooltipHeading',
                )}
                description={t(
                  'designs:designGallery.templateEditor.bottomToolbar.selectedCompilationContext.tooltipDescription',
                )}
              />
            }
            position="top"
          >
            <CopyToClipboard value={compilationContext.snapshotId} />
          </Tooltip>
        ) : (
          <P bold={true}>
            {t('designs:designGallery.templateEditor.bottomToolbar.selectedCompilationContext.notLoaded')}
          </P>
        )}

        <Gap right={10} />
      </Box>
    );
  };

  return (
    <BottomBar>
      {isPreviewMode(versionId) ? (
        <SavePanel
          onSaveButtonClicked={restoreTemplateVersion}
          saveButtonLabel={t('designs:designGallery.templateEditor.bottomToolbar.restoreTemplate')}
          enableSaveButton={true}
          shortcutOptions={options()}
          paddingLeft="13px"
        />
      ) : (
        <SaveBar
          onSaveButtonClicked={handleSaveButtonClicked}
          onExitButtonClicked={() => handleExitButtonClicked()}
          saveButtonLabel={t('designs:designGallery.templateEditor.bottomToolbar.saveTemplate')}
          exitButtonLabel={t('designs:designGallery.templateEditor.bottomToolbar.exit')}
          onDiscardButtonClicked={discardChanges}
          enableSaveButton={hasUnsavedChanges}
          shortcutOptions={options()}
          paddingLeft="13px"
        />
      )}
      <TemplateEditorSettingsModal settingsModalOpen={settingsModalOpen} setSettingsModalOpen={setSettingsModelOpen} />
      <TemplateEditorSaveCommentModal
        commentModalOpen={commentsModalOpen}
        setCommentModalOpen={setCommentsModelOpen}
      ></TemplateEditorSaveCommentModal>
      <TemplateEditorUnsavedChangesModal
        unsavedChangesModalOpen={unsavedChangesModalOpen}
        setUnsavedChangesModalOpen={setUnsavedChangesModalOpen}
        redirectUrl={generatePath(TemplateManagementRoute.HOME, { siteId, channel })}
      />
    </BottomBar>
  );
};
